export const cgmTestUsability = `

<br/>


<p><strong>Disclaimer:</strong> This test is not sponsored or influenced by a manufacturer. Reliability of measurement was checked with data from 8–10 daily blood checks using a Contour Plus One glucometer. CGMs were tested from 9 to 24 December 2025.</p>

  <div style="display: flex; flex-direction: row; justify-content: center; gap: 2em; margin-top: 1.5em; margin-bottom: 1em;">
    <a
      href="/blog/details/Insights-from-Testing-10-CGMs---How-They-Stack-Up-in-Daily-Use"
      rel="noopener"
      style="color: #1976d2; font-size: 1em; text-align: center; text-decoration: underline; font-weight: 500; padding: 0.75em 1.5em; border-radius: 6px; background: rgba(25, 118, 210, 0.07); transition: background 0.2s; min-width: 290px; display: inline-block;"
      data-mouseover-bg="rgba(25, 118, 210, 0.13)"
      data-mouseout-bg="rgba(25, 118, 210, 0.07)"
    >
      <span style="font-weight: 700;">Looking for numbers? See the comparison on CGM reliability and accuracy &rarr;</span>
    </a>
    <a
      href="/blog/details/Where-Does-Your-Glucose-Data-Really-Go---A-Look-at-CGM-Privacy-and-Server-Locations"
      rel="noopener"
      style="color: #1976d2; font-size: 1em; text-align: center; text-decoration: underline; font-weight: 500; padding: 0.75em 1.5em; border-radius: 6px; background: rgba(25, 118, 210, 0.07); transition: background 0.2s; min-width: 290px; display: inline-block;"
      data-mouseover-bg="rgba(25, 118, 210, 0.13)"
      data-mouseout-bg="rgba(25, 118, 210, 0.07)"
    >
      <span style="font-weight: 700;">Interested in privacy and data storage? See the comparison on CGM privacy and data storage &rarr;</span>
    </a>
  </div>
  
<div style="display: flex; flex-direction: column; align-items: center; padding: 1.5rem 0;">
  <iframe 
    width="560" 
    height="315" 
    src="https://www.youtube.com/embed/AtkWh9RAjtY" 
    title="CGM Sensors Overview Video" 
    frameBorder="0" 
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
    referrerPolicy="strict-origin-when-cross-origin" 
    allowFullScreen
  ></iframe>


</div>


<h3 style="margin-top: 24px; margin-bottom: 16px;">Design</h3>

<p>CGM sensors have improved significantly in quality and competition. Most aren't disasters anymore, unlike a few years ago. Here is a comparison on packaging design.</p>

<ul style="line-height: 1.8;">
  <li><strong>Best Workmanship and Materials:</strong> Caresens Air and Dexcom G7. Caresens surprised with high-quality packaging, double seals that don't tear, grooved grips on the applicator, and a muffled sound during application (like a luxury car door closing).</li>
  <li><strong>Worst Applicator:</strong> Yuwell Anytime – small lock requiring fine motor skills and strength, stiff operation, cheap plastics, and awkward transmitter clicking while turning. Challenging for those with limited mobility.</li>
  <li><strong>Least Waste:</strong> Dexcom G7.</li>
  <li><strong>Size and Comfort:</strong> Size isn't a big deal if it performs well. Yuwell, though largest and thickest, is comfortable due to its rounded shape. Yuwell and SmartGuide have the thickest, hardest "wire."</li>
  <li><strong>Overpatches:</strong> Not used to test default adhesives. Using them would negate size advantages claimed by some brands.</li>
</ul>

<p style="text-align: center; margin: 2rem 0;">
  <img 
    src="/assets/blogs/sensorcomparison/sensorsizesandapplicators.png" 
    alt="Sensor Sizes and Applicators Comparison" 
    style="max-width: 100%; height: auto; border-radius: 12px; box-shadow: 0 2px 12px rgba(0,0,0,0.08);" 
  />
    <span style="display: block; margin-top: 0.5em; color: #444; font-size: 0.75em; font-weight: bold;">
    Overview of Sensor Sizes and Applicators
  </span>
</p>


<br/>

<h3 style="margin-top: 24px; margin-bottom: 16px;">Apps</h3>

<p>The apps vary in maturity.</p>

<ul style="line-height: 1.8;">
  <li><strong>Most Native Integrations:</strong> Syai (connects to AAPS, Nightscout, xDrip, Garmin, WearOS, Karaz Health, Health Connect, Novo Pen, smart glucometers) and Caresens (Samsung Health, Xdrip, Carelevo, Dia:conn, Cloud loop, DANA One, AnyDana Plus). Hippo sends to Xdrip natively. Others possible via workarounds; SmartGuide added to xDrip on Dec 19, 2025.</li>
  <li><strong>Reports and Statistics:</strong> Dexcoms and Caresens need extra apps for stats (inconvenient…). Syai, Accu-Chek SmartGuide, and Yuwell display ambulatory glucose profiles reports, percentiles, and hypo frequencies directly.</li>
  <li><strong>Calibrations:</strong> Direct calibration in Dexcoms and Caresens. FSL calibration via third-party apps (not native LibreLink). Syai considers fingerstick data somewhat. Others allow entry but don't use it.</li>
  <li><strong>Alarms and Sounds:</strong> Hippo (An alarm you really don't want to hear will override the phone's silent mode under all circumstances, and if the GL is below 3, it will sound every 3 minutes; if the GL is above 3, it can be postponed for a selected number of minutes). Most pleasant alarm: Dexcom G7.</li>
  <li><strong>Measurement Frequency:</strong> FSL every 1 min, Hippo/Yuwell every 3 min, others 5 min.</li>
  <li><strong>Bad Translations and Messages:</strong><br/>
1) Yuwell shows mg/dL in lows despite mmol/L setting, odd messages like "No relatives can invite them," half-Czech/half-Turkish animations.<br/>
2) LibreLink mistranslates warmup time.<br/>
3) Caresens offers Samsung Health on non-Samsung phones<br/>
4) Hippo has Chinese characters in some pages. Valid as of Dec 22, 2025; may be fixed.</li>
</ul>

<p style="text-align: center; margin: 2rem 0;">
  <img 
    src="/assets/blogs/sensorcomparison/hippochinesecharacters.png" 
    alt="Hippo CGM app showing Chinese characters in some pages" 
    style="max-width: 100%; height: auto; border-radius: 12px; box-shadow: 0 2px 12px rgba(0,0,0,0.08);" 
  />
  <br/>
  <span style="display: block; margin-top: 0.5em; color: #444; font-size: 0.75em; font-weight: bold;">
    Chinese characters appear in the Hippo app on some pages due to incomplete translations.
  </span>
</p>


<br/>

<h3 style="margin-top: 24px; margin-bottom: 16px;">Special features</h3>

<ul style="line-height: 1.8;">
  <li>Caresense and LibreLink can read out sensor values aloud (Caresens also alerts)</li>
  <li>SmartGuide distinguishes between trend mode (when glucose changes rapidly) and treatment mode (when glucose does not change and should be reliable), but predictions (not particularly useful, just a very broad extrapolation of the trend) require an additional app.</li>
  <li>SmartGuide support added to xDrip on December 19, 2025</li>
  <li>Hippo, Yuwell, and Smartguide cannot display glucose in notifications (top drop-down menu), only a meaningless message saying "the system is running," "tap to view more information" and "the application is waiting for the next glucose value from the CGM sensor" is displayed — something that takes up three text lines, while the gluocse value would only be a couple of numbers.</li>
  <li>Syai: blind mode can be set directly in the app; it does not require a different type of sensor or another app.</li>
  <li>Although Yuwell is the largest and thickest, it is very comfortable to wear because it is rounded.</li>
</ul>


<br/>

<h3 style="margin-top: 24px; margin-bottom: 16px;">Other Practical Aspects</h3>

<ul style="line-height: 1.8;">
  <li><strong>Patches and Adhesion:</strong> No sensors fell off. Visually worst at end: Both Dexcoms and Accu-Chek SmartGuide (slight skin reaction from SmartGuide only). Tested with normal activities (showering, sweating, etc.).</li>
  <li><strong>Signal Strength:</strong> Tested in open space; Yuwell lost at 8m, others held to 14m.</li>
  <li><strong>Ended Prematurely:</strong> Glunovo D3 (right after insertion), Dexcom G7 (8d 7h, error), Hippo H1 (8d 11h, manual end due to LO readings), Caresens Air (14d 10h).</li>
  <li><strong>Screen Reader Optimization:</strong> Didn't test screen reader optimization or font size changes (but might be relevant for visually impaired).</li>
  <li><strong>Feedback:</strong> Some led to quick updates from developers when I gave feedback to the company (e.g., Hippo and Syai fixed issues overnight, including a privacy policy update). Big players like Dexcom and Abbott are slower in their response.</li>
</ul>


<br/>
<h3 style="margin-top: 24px; margin-bottom: 16px;">Favorites for Looping</h3>

<p>Subjective favorites for a Dexcom G6 successor in an open-source loop: Syai and Caresens.</p>

<br/>

<p style="font-size: 0.9em; color: #666; font-style: italic;">
Written by <a href="/blog/details/Introducing-Luk%C3%A1%C5%A1-Tr%C4%8Dka" target="_blank" rel="noopener" style="color: #1976d2; text-decoration: underline; font-weight: 500;">Lukáš Trčka</a>, reviewed by <a href="/blog/details/Introducing-Kelvin-Kramp" target="_blank" rel="noopener" style="color: #1976d2; text-decoration: underline; font-weight: 500;">Kelvin Kramp</a> (MD, PhD).
</p>

<div style="display: flex; justify-content: center; padding: 1.5rem 0;">
<img src="https://miro.medium.com/v2/resize:fit:1400/format:webp/1*0_i4kx_n4GqjBQ5vSF9E4w.png" alt="The Opensource.clinic team" style="max-width: 100%; height: auto;" />
</div>


<!-- blog-contact-footer -->
`;
